import React from 'react';
import {
  NestedOptions,
  ProjectOption,
  useAllProjectOptions,
} from '@/hooks/useProjectOptions';
import { useOptionalFilterState } from '@/hooks/useFilter';
import {
  BilderitModuleId,
  BilderitReportType,
  useSubscribeToBilderitReportMutation,
} from '@/generated/codegen';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from '@/components/ui/select';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { ChevronsUpDown, Loader2Icon, MailPlusIcon } from 'lucide-react';
import { Button, ButtonProps } from '@/components/ui/button';
import { Field, FieldError, FieldSet, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BILDERIT_REPORT_TYPES } from './AppSchema';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

export type NewBilderitReportsSubscriptionButtonProps = ButtonProps & {
  reportType?: BilderitReportType;
  className?: string;
};

type ReportTypeMeta = (typeof BILDERIT_REPORT_TYPES)[number];
export type ReportSubscriptionScope = 'project' | 'company';

export const getAllowedReportScopes = (
  reportTypeMeta?: Pick<ReportTypeMeta, 'scope'>
): ReportSubscriptionScope[] => {
  if (!reportTypeMeta) return [];
  if (reportTypeMeta.scope === 'both') return ['project', 'company'];
  return [reportTypeMeta.scope];
};

export const getScopedContextGroups = (
  groups: NestedOptions[],
  scope: ReportSubscriptionScope,
  companyId?: number
): NestedOptions[] =>
  groups
    .filter((group) => companyId == null || group.id === companyId)
    .map((group) => ({
      ...group,
      options: group.options.filter((option) =>
        scope === 'company'
          ? !option.value.project_id
          : !!option.value.project_id
      ),
    }))
    .filter((group) => group.options.length > 0);

export const findDefaultScopedContext = (
  groups: NestedOptions[],
  filter: { company_id?: number | null; project_id?: number | null },
  scope: ReportSubscriptionScope
): ProjectOption | undefined => {
  if (!filter.company_id) return undefined;

  const companyGroup = groups.find((group) => group.id === filter.company_id);
  if (!companyGroup) return undefined;

  if (scope === 'company') {
    return companyGroup.options.find((option) => !option.value.project_id);
  }

  if (!filter.project_id) return undefined;

  return companyGroup.options.find(
    (option) => option.value.project_id === filter.project_id
  );
};

export const NewBilderitReportsSubscriptionButton: React.FC<
  NewBilderitReportsSubscriptionButtonProps
> = ({ reportType, className, ...props }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { isFeatureEnabled } = useFeatureFlags();
  const reportTypeMeta = reportType
    ? BILDERIT_REPORT_TYPES.find((r) => r.value === reportType)
    : undefined;
  const tooltipLabel =
    reportTypeMeta?.periodicity === 'monthly'
      ? 'Reportes mensuales'
      : reportTypeMeta?.periodicity === 'daily'
        ? 'Reportes diarios'
        : 'Reportes semanales';

  return isFeatureEnabled(BilderitModuleId.WeeklyReports) ? (
    <Tooltip>
      <TooltipContent>{tooltipLabel}</TooltipContent>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="md-icon"
          {...props}
          onClick={(e) => {
            props.onClick?.(e);
            setDialogOpen(true);
          }}
          className={className}
        >
          <MailPlusIcon size={18} />
        </Button>
      </TooltipTrigger>
      {dialogOpen && (
        <NewBilderitReportsSubscriptionForm
          open={dialogOpen}
          setOpen={setDialogOpen}
          reportType={reportType}
        />
      )}
    </Tooltip>
  ) : null;
};

// I separate it here so it's easier to reset the state on open/close

export type NewBilderitReportsSubscriptionFormProps = {
  reportType?: BilderitReportType;
  open: boolean;
  setOpen: (o: boolean) => void;
};

export const NewBilderitReportsSubscriptionForm: React.FC<
  NewBilderitReportsSubscriptionFormProps
> = ({ reportType, open, setOpen }) => {
  const filter = useOptionalFilterState();
  const companies = useAllProjectOptions();
  const [contextOpen, setContextOpen] = React.useState(false);
  const [context, setContext] = React.useState<ProjectOption | undefined>();
  const [type, setType] = React.useState<BilderitReportType | undefined>(
    reportType
  );
  const [scope, setScope] = React.useState<ReportSubscriptionScope>('project');
  const [email, setEmail] = React.useState('');
  const [subscribe, { loading, error }] = useSubscribeToBilderitReportMutation({
    refetchQueries: ['GetMyReportSubscriptions'],
  });
  const reportTypes = BILDERIT_REPORT_TYPES;

  const selectedReportTypeMeta = type
    ? reportTypes.find((r) => r.value === type)
    : undefined;
  const allowedScopes = getAllowedReportScopes(selectedReportTypeMeta);
  const preferredScope: ReportSubscriptionScope =
    selectedReportTypeMeta?.scope === 'both'
      ? filter.value.project_id
        ? 'project'
        : 'company'
      : (allowedScopes[0] ?? 'project');
  const scopedContextGroups = React.useMemo(
    () =>
      getScopedContextGroups(
        companies,
        scope,
        filter.value.company_id ?? undefined
      ),
    [companies, filter.value.company_id, scope]
  );
  const isMonthly = selectedReportTypeMeta?.periodicity === 'monthly';
  const isDaily = selectedReportTypeMeta?.periodicity === 'daily';

  React.useEffect(() => {
    if (!allowedScopes.length) return;
    if (!allowedScopes.includes(scope)) {
      setScope(preferredScope);
    }
  }, [allowedScopes, preferredScope, scope]);

  React.useEffect(() => {
    setContext(
      findDefaultScopedContext(scopedContextGroups, filter.value, scope)
    );
  }, [filter.value, scope, scopedContextGroups]);

  const canSubmit = !!context && !!type && !!email;
  const scopeLabel = scope === 'company' ? 'Compañía' : 'Proyecto';
  const emptyScopeMessage =
    scope === 'company'
      ? 'No tenes acceso a ninguna compañía con alcance de compañía.'
      : 'No tenes acceso a ningún proyecto.';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <form
          // action="javascript:void(0);"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;

            const company_id = context!.value.company_id;
            const project_id =
              scope === 'project' ? context!.value.project_id : undefined;

            subscribe({
              variables: {
                input: {
                  company_id,
                  ...(project_id != null ? { project_id } : {}),
                  report_type: type!,
                  email,
                },
              },
            })
              .then(() => {
                setContext(undefined);
                setType(undefined);
                setEmail('');
                setOpen(false);
              })
              .catch((error) => {
                console.error('Subscription error:', error);
              });
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {isMonthly
                ? 'Nuevo Reporte Mensual'
                : isDaily
                  ? 'Nuevo Reporte Diario'
                  : 'Nuevo Reporte Semanal'}
            </DialogTitle>
            <DialogDescription>
              {isMonthly
                ? 'Suscribite para recibir novedades en tu email todos los meses.'
                : isDaily
                  ? 'Suscribite para recibir el resumen de novedades en tu email cada día.'
                  : 'Suscribite para recibir novedades en tu email todas las semanas.'}
            </DialogDescription>
          </DialogHeader>
          <FieldSet className="py-6">
            {selectedReportTypeMeta?.scope === 'both' && (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="scope">Alcance</FieldLabel>
                <Select
                  value={scope}
                  onValueChange={(value) =>
                    setScope(value as ReportSubscriptionScope)
                  }
                >
                  <SelectTrigger id="scope" className="w-[250px]">
                    <SelectValue placeholder="Alcance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="project">Proyecto</SelectItem>
                      <SelectItem value="company">Compañía</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            )}
            {type && (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="context">{scopeLabel}</FieldLabel>
                <Popover open={contextOpen} onOpenChange={setContextOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      id="context"
                      variant="outline"
                      role="combobox"
                      aria-expanded={contextOpen}
                      className="justify-between w-[250px]"
                    >
                      <div className="overflow-hidden text-ellipsis">
                        {context ? context.label : `${scopeLabel}...`}
                      </div>
                      <ChevronsUpDown className="opacity-50 size-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar..." />
                      <CommandList>
                        <CommandEmpty>{emptyScopeMessage}</CommandEmpty>
                        {scopedContextGroups.map((company) => (
                          <CommandGroup
                            key={company.label}
                            heading={company.label}
                          >
                            {company.options.map((option) => (
                              <CommandItem
                                key={JSON.stringify(option.value)}
                                onSelect={() => {
                                  setContext(option);
                                  setContextOpen(false);
                                }}
                                keywords={[option.label, company.label]}
                              >
                                {option.label}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        ))}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </Field>
            )}
            <Field orientation="horizontal">
              <FieldLabel htmlFor="type">Tipo</FieldLabel>
              <Select
                value={type}
                onValueChange={(value) => setType(value as BilderitReportType)}
              >
                <SelectTrigger id="type" className="w-[250px]">
                  <SelectValue placeholder="Tipo de Reporte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {reportTypes.map(({ label, value }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field orientation="horizontal">
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email"
                className="w-[250px]"
              />
            </Field>
            <FieldError>
              {error?.message.replace(
                'Subscription already exists',
                'Esta subscripción ya existe'
              )}
            </FieldError>
          </FieldSet>
          <DialogFooter>
            <Button type="submit" disabled={!canSubmit || loading}>
              {loading ? <Loader2Icon className="animate-spin" /> : 'Suscribir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
